import { useCallback, useEffect, useRef, useState } from 'react';
import { GameShell, useGameClock, useReducedMotion } from './GameShell.js';

interface Gate {
  question: string;
  answer: number;
  options: number[];
}

/**
 * Three lanes, one answer, steer into it.
 *
 * The fact is overhead and the lanes carry the options, so the whole decision
 * is "which way" rather than "type this". That is what makes it a game: the
 * child already knows the fact, and the fun is in being quick about it.
 *
 * Driving into the wrong lane does not crash anything. The gate just goes past
 * and the next one comes - a breather that punishes is not a breather.
 */
export function QuokkaRacer({
  gates,
  seconds,
  onFinish,
}: {
  gates: Gate[];
  seconds: number;
  onFinish: (score: number) => void;
}) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [lane, setLane] = useState(1);
  const [cleared, setCleared] = useState(0);
  const [flash, setFlash] = useState<'hit' | 'miss' | null>(null);
  const answered = useRef(false);

  const gate = gates[index % Math.max(1, gates.length)];
  const finish = useCallback(() => onFinish(cleared), [onFinish, cleared]);
  const left = useGameClock(seconds, finish);

  // How long this gate takes to reach the car. Also the animation duration,
  // so what the child SEES and what the clock does are the same number.
  // Keyed to the gate NUMBER, not the score: tied to `cleared` it changed
  // mid-gate every time the child got one right, which restarted both the
  // timer and the animation under them.
  const approach = reduced ? 4200 : Math.max(1600, 3400 - index * 70);

  // The gate arrives whether or not the child chose. Sitting still is a miss,
  // which is what keeps it moving without ever being harsh about it.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!answered.current) setFlash('miss');
      answered.current = false;
      setIndex((n) => n + 1);
      setTimeout(() => setFlash(null), 250);
    }, approach);
    return () => clearTimeout(timer);
  }, [index, approach]);

  const choose = (chosen: number, laneIndex: number) => {
    if (answered.current || !gate) return;
    answered.current = true;
    setLane(laneIndex);

    if (chosen === gate.answer) {
      setCleared((n) => n + 1);
      setFlash('hit');
    } else {
      setFlash('miss');
    }

    // Move on straight away rather than making them watch the rest of the
    // gate's approach. Waiting after you have already answered is most of
    // what made this feel like nothing was happening.
    window.setTimeout(() => {
      setFlash(null);
      answered.current = false;
      setIndex((n) => n + 1);
    }, 420);
  };

  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      if (!gate) return;
      if (event.key === 'ArrowLeft') choose(gate.options[0] as number, 0);
      if (event.key === 'ArrowUp') choose(gate.options[1] as number, 1);
      if (event.key === 'ArrowRight') choose(gate.options[2] as number, 2);
    };
    window.addEventListener('keydown', keys);
    return () => window.removeEventListener('keydown', keys);
  });

  if (!gate) return null;

  return (
    <GameShell
      name="Quokka Racer"
      scoreLabel="Gates"
      score={cleared}
      secondsLeft={left}
      totalSeconds={seconds}
      onQuit={finish}
    >
      <div className="racer" data-flash={flash ?? undefined}>
        <p className="racer__question">{gate.question}</p>

        <div className="racer__road">
          <span className="racer__line racer__line--a" data-moving={!reduced} />
          <span className="racer__line racer__line--b" data-moving={!reduced} />

          {/* Keyed on the gate number so the run restarts for each new gate:
              the row travels DOWN the road towards the car, which is the
              whole reason it feels like driving rather than a quiz. */}
          <div
            key={index}
            className="racer__gates"
            style={{ ['--approach' as string]: `${approach}ms` }}
          >
            {gate.options.map((option, i) => (
              <button
                key={`${index}-${option}`}
                type="button"
                className="racer__gate"
                onClick={() => choose(option, i)}
              >
                {option}
              </button>
            ))}
          </div>

          <span className="racer__car" data-lane={lane} aria-label="Your car" />
        </div>
      </div>
    </GameShell>
  );
}
